import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import b1 from "../../assets/b1.jpg";

// ─── Global Styles ────────────────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@200;300;400&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --cream:     #F7F2EA;
    --cream-mid: #EDE5D8;
    --brown-dk:  #3D2E1A;
    --brown-md:  #5A4228;
    --gold:      #8C6940;
    --gold-lt:   rgba(140,105,64,0.18);
    --gold-xlt:  rgba(140,105,64,0.08);
    --text-muted: rgba(90,70,45,0.55);
    --border:    rgba(160,125,85,0.18);
  }

  html, body { background: var(--cream); overflow-x: hidden; }
`;

// ─── Keyframes ────────────────────────────────────────────────────────────────
const scrollPulse = keyframes`
  0%,100% { transform: scaleY(1); opacity: 0.35; }
  50%      { transform: scaleY(0.45); opacity: 0.8; }
`;

const circleSpin = keyframes`
  from { transform: translate(-50%,-50%) rotate(0deg); }
  to   { transform: translate(-50%,-50%) rotate(360deg); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const Wrapper = styled.section`
  position: relative;
  width: 100%;
  min-height: 100svh;
  background: var(--cream);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  font-family: "Jost", sans-serif;
  /* Room for bottom strip on small screens */
  padding: 6rem 1.5rem 7rem;

  @media (min-width: 601px) {
    padding: 5rem 2rem 6rem;
  }
`;

const GrainOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.045'/%3E%3C/svg%3E");
  opacity: 0.5;
  pointer-events: none;
  z-index: 1;
`;

const CircleBg = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;

  /* Scale rings down on mobile so they don't overflow weirdly */
  @media (max-width: 600px) {
    transform: translate(-50%, -50%) scale(0.55);
  }

  @media (min-width: 601px) and (max-width: 900px) {
    transform: translate(-50%, -50%) scale(0.75);
  }
`;

const RotatingRing = styled.div`
  position: absolute;
  width: 680px;
  height: 680px;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  border: 1px dashed rgba(140, 105, 64, 0.1);
  transform: translate(-50%, -50%) rotate(0deg);
  animation: ${circleSpin} 60s linear infinite;
  z-index: 1;
  pointer-events: none;

  @media (max-width: 600px) {
    width: 360px;
    height: 360px;
  }

  @media (min-width: 601px) and (max-width: 900px) {
    width: 520px;
    height: 520px;
  }
`;

const Nav = styled(motion.nav)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 2.5rem 4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;

  @media (max-width: 600px) {
    padding: 1.6rem 1.5rem;
  }
`;

const NavLogo = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-weight: 300;
  font-size: 1.05rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--brown-dk);
`;

const NavLinks = styled.ul`
  display: flex;
  gap: 2.5rem;
  list-style: none;

  @media (max-width: 600px) {
    display: none;
  }
`;

const NavLink = styled.a`
  text-decoration: none;
  font-family: "Jost", sans-serif;
  font-weight: 300;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-muted);
  transition: color 0.3s;
  &:hover {
    color: var(--brown-dk);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 5;
  text-align: center;
  padding: 0 1rem;
  width: 100%;
  max-width: 680px;
`;

const Eyebrow = styled(motion.div)`
  font-family: "Jost", sans-serif;
  font-weight: 200;
  font-size: 0.68rem;
  letter-spacing: 0.38em;
  text-transform: uppercase;
  color: rgba(140, 105, 64, 0.8);
  margin-bottom: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;

  &::before,
  &::after {
    content: "";
    display: inline-block;
    width: 40px;
    height: 1px;
    background: rgba(140, 105, 64, 0.35);
    flex-shrink: 0;
  }

  @media (max-width: 400px) {
    font-size: 0.58rem;
    letter-spacing: 0.25em;
    gap: 0.8rem;
    &::before,
    &::after {
      width: 24px;
    }
  }
`;

const Headline = styled.h1`
  font-family: "Cormorant Garamond", serif;
  font-weight: 300;
  font-size: clamp(3.2rem, 10vw, 7.5rem);
  line-height: 0.88;
  color: var(--brown-dk);
  letter-spacing: -0.01em;
  overflow: hidden;
`;

const HeadlineLine = styled(motion.span)`
  display: block;
`;

const ItalicGold = styled.span`
  font-style: italic;
  color: var(--gold);
`;

const Ornament = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem auto 0;
  gap: 0.8rem;
  opacity: 0.4;
`;

const OrnamentLine = styled.div`
  width: 50px;
  height: 1px;
  background: var(--gold);

  @media (max-width: 400px) {
    width: 32px;
  }
`;

const OrnamentDiamond = styled.div`
  width: 6px;
  height: 6px;
  border: 1px solid var(--gold);
  transform: rotate(45deg);
`;

const Tagline = styled(motion.p)`
  font-family: "Jost", sans-serif;
  font-weight: 200;
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-top: 2.2rem;
  line-height: 1.8;

  @media (max-width: 400px) {
    font-size: 0.62rem;
    letter-spacing: 0.16em;
  }
`;

const CTAGroup = styled(motion.div)`
  margin-top: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 1.4rem;
    margin-top: 2.5rem;
  }
`;

const BtnPrimary = styled(motion.button)`
  font-family: "Jost", sans-serif;
  font-weight: 300;
  font-size: 0.68rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--cream);
  background: var(--brown-dk);
  border: none;
  padding: 1rem 2.8rem;
  cursor: pointer;
  outline: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  @media (max-width: 480px) {
    width: 100%;
    padding: 1rem 2rem;
    text-align: center;
  }
`;

const BtnGhost = styled(motion.button)`
  font-family: "Jost", sans-serif;
  font-weight: 300;
  font-size: 0.68rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(90, 70, 45, 0.75);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  outline: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: gap 0.3s;
  &:hover {
    gap: 1.1rem;
  }
`;

// ─── Floating frames — hide on small mobile, reposition on tablet ─────────────
const FloatFrame = styled(motion.div)`
  position: absolute;
  border: 1px solid var(--border);
  z-index: 2;
  overflow: hidden;

  /* Hide all frames on very small screens */
  @media (max-width: 480px) {
    display: none;
  }

  /* Scale down on tablet */
  @media (min-width: 481px) and (max-width: 900px) {
    transform-origin: top left;
    scale: 0.8;
  }
`;

const FrameInner = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: flex-end;
  padding: 0.8rem;
`;

const FrameImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
`;

const FrameScrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(61, 46, 26, 0.55) 0%,
    rgba(61, 46, 26, 0.1) 45%,
    transparent 100%
  );
`;

const FrameLabel = styled.span`
  position: relative;
  z-index: 2;
  font-family: "Cormorant Garamond", serif;
  font-style: italic;
  font-size: 0.62rem;
  letter-spacing: 0.12em;
  color: rgba(247, 242, 234, 0.85);
  text-transform: uppercase;
`;

// ─── Frame position wrappers — let us reposition per breakpoint ───────────────
const FrameTopLeft = styled(FloatFrame)`
  top: 14%;
  left: 7%;

  @media (min-width: 481px) and (max-width: 900px) {
    top: 10%;
    left: 2%;
  }
`;

const FrameBottomLeft = styled(FloatFrame)`
  bottom: 17%;
  left: 10%;

  @media (min-width: 481px) and (max-width: 900px) {
    bottom: 12%;
    left: 2%;
  }
`;

const FrameTopRight = styled(FloatFrame)`
  top: 17%;
  right: 7%;

  @media (min-width: 481px) and (max-width: 900px) {
    top: 10%;
    right: 2%;
  }
`;

const FrameBottomRight = styled(FloatFrame)`
  bottom: 19%;
  right: 9%;

  @media (min-width: 481px) and (max-width: 900px) {
    bottom: 12%;
    right: 2%;
  }
`;

const BottomStrip = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem 4rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  z-index: 10;

  @media (max-width: 600px) {
    padding: 1.2rem 1.5rem;
    align-items: center;
  }
`;

const LocationTag = styled.p`
  font-family: "Jost", sans-serif;
  font-weight: 200;
  font-size: 0.6rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-muted);

  @media (max-width: 400px) {
    font-size: 0.52rem;
    letter-spacing: 0.15em;
  }
`;

const ScrollHint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  font-family: "Jost", sans-serif;
  font-weight: 200;
  font-size: 0.58rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(90, 70, 45, 0.35);

  /* Hide on very small to save space */
  @media (max-width: 380px) {
    display: none;
  }
`;

const ScrollLine = styled.div`
  width: 1px;
  height: 38px;
  background: rgba(90, 70, 45, 0.2);
  animation: ${scrollPulse} 2s ease-in-out infinite;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1.5rem;

  @media (max-width: 400px) {
    gap: 1rem;
  }
`;

const SocialLink = styled.a`
  text-decoration: none;
  font-family: "Jost", sans-serif;
  font-weight: 200;
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--text-muted);
  transition: color 0.3s;
  &:hover {
    color: var(--brown-dk);
  }

  @media (max-width: 400px) {
    font-size: 0.52rem;
    letter-spacing: 0.14em;
  }
`;

// ─── Animation Variants ───────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  },
};

const lineReveal = {
  hidden: { opacity: 0, y: 60 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] },
  },
};

const circleVariant = {
  hidden: { scale: 0, opacity: 0 },
  show: (delay) => ({
    scale: 1,
    opacity: 1,
    transition: { duration: 1.6, ease: [0.16, 1, 0.3, 1], delay },
  }),
};

const frameVariant = {
  hidden: { opacity: 0, y: 28 },
  show: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1], delay },
  }),
};

// ─── Frame image sources ──────────────────────────────────────────────────────
const FRAME_IMAGES = {
  bridal: b1,
  editorial: b1,
  portrait: b1,
  events: b1,
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function Banner() {
  return (
    <>
      <GlobalStyle />
      <Wrapper>
        <GrainOverlay />

        {/* Background rings */}
        <CircleBg
          custom={0.2}
          variants={circleVariant}
          initial="hidden"
          animate="show"
          style={{
            width: 700,
            height: 700,
            border: "1px solid rgba(160,125,85,0.13)",
            borderRadius: "50%",
          }}
        />
        <CircleBg
          custom={0.4}
          variants={circleVariant}
          initial="hidden"
          animate="show"
          style={{
            width: 500,
            height: 500,
            border: "1px solid rgba(160,125,85,0.09)",
            borderRadius: "50%",
          }}
        />
        <CircleBg
          custom={0.6}
          variants={circleVariant}
          initial="hidden"
          animate="show"
          style={{
            width: 300,
            height: 300,
            border: "1px solid rgba(160,125,85,0.07)",
            borderRadius: "50%",
          }}
        />
        <RotatingRing />

        {/* Floating frames */}
        <FrameTopLeft
          custom={1.0}
          variants={frameVariant}
          initial="hidden"
          animate="show"
          style={{ width: 140, height: 185 }}
        >
          <FrameInner>
            <FrameImage src={FRAME_IMAGES.bridal} alt="Bridal makeup" />
            <FrameScrim />
            <FrameLabel>Bridal</FrameLabel>
          </FrameInner>
        </FrameTopLeft>

        <FrameBottomLeft
          custom={1.2}
          variants={frameVariant}
          initial="hidden"
          animate="show"
          style={{ width: 105, height: 135 }}
        >
          <FrameInner>
            <FrameImage src={FRAME_IMAGES.editorial} alt="Editorial makeup" />
            <FrameScrim />
            <FrameLabel>Editorial</FrameLabel>
          </FrameInner>
        </FrameBottomLeft>

        <FrameTopRight
          custom={1.1}
          variants={frameVariant}
          initial="hidden"
          animate="show"
          style={{ width: 125, height: 165 }}
        >
          <FrameInner>
            <FrameImage src={FRAME_IMAGES.portrait} alt="Portrait makeup" />
            <FrameScrim />
            <FrameLabel>Portrait</FrameLabel>
          </FrameInner>
        </FrameTopRight>

        <FrameBottomRight
          custom={1.3}
          variants={frameVariant}
          initial="hidden"
          animate="show"
          style={{ width: 95, height: 120 }}
        >
          <FrameInner>
            <FrameImage src={FRAME_IMAGES.events} alt="Events makeup" />
            <FrameScrim />
            <FrameLabel>Events</FrameLabel>
          </FrameInner>
        </FrameBottomRight>

        {/* Hero */}
        <HeroContent>
          <Eyebrow
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.6 }}
          >
            Hair &amp; Makeup Artist
          </Eyebrow>

          <Headline>
            <HeadlineLine
              variants={lineReveal}
              initial="hidden"
              animate="show"
              transition={{ delay: 0.75 }}
            >
              Artistry that
            </HeadlineLine>
            <HeadlineLine
              variants={lineReveal}
              initial="hidden"
              animate="show"
              transition={{ delay: 0.92 }}
            >
              <ItalicGold>Speaks</ItalicGold>
            </HeadlineLine>
            <HeadlineLine
              variants={lineReveal}
              initial="hidden"
              animate="show"
              transition={{ delay: 1.1 }}
            >
              For itself
            </HeadlineLine>
          </Headline>

          <Ornament
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 1.3 }}
          >
            <OrnamentLine />
            <OrnamentDiamond />
            <OrnamentLine />
          </Ornament>

          <Tagline
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 1.45 }}
          >
            Professional make up services for every occasion!
          </Tagline>

          <CTAGroup
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 1.6 }}
          >
            <BtnPrimary
              whileHover={{ background: "#5A4228", y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              Book a Session
            </BtnPrimary>
            <BtnGhost whileHover={{ gap: "1.1rem" }}>
              View Portfolio <span>→</span>
            </BtnGhost>
          </CTAGroup>
        </HeroContent>

        {/* Bottom strip */}
        <BottomStrip
          variants={fadeUp}
          initial="hidden"
          animate="show"
          transition={{ delay: 1.8 }}
        >
          <LocationTag>Based in München, Germany</LocationTag>
          <ScrollHint>
            <ScrollLine />
            Scroll
          </ScrollHint>
          <SocialLinks>
            <SocialLink href="#">Instagram</SocialLink>
            <SocialLink href="#">Pinterest</SocialLink>
          </SocialLinks>
        </BottomStrip>
      </Wrapper>
    </>
  );
}
